import React, { useMemo } from "react";
import { Target } from "lucide-react";
import CommonSelect from "@/shared/components/ui/Select/CommonSelect";
import { useGetCampaigns } from "@/features/campaigns/hooks/useCampaign";
import type { SearchCampaign } from "../../types";

const CommonSelectComponent = CommonSelect as React.ComponentType<any>;

interface CampaignSearchPickerProps {
  onPick: (campaign: SearchCampaign) => void;
  disabled?: boolean;
}

/** "Find contacts for a campaign": searches using the picked campaign's ICP. */
export default function CampaignSearchPicker({ onPick, disabled }: CampaignSearchPickerProps) {
  const { data, isLoading } = (useGetCampaigns as any)(1, 100, "", true);
  const campaigns: any[] = useMemo(() => data?.campaigns ?? [], [data]);
  const options = useMemo(
    () => campaigns.map((c) => ({ label: c.name || "Untitled campaign", value: String(c.id) })),
    [campaigns],
  );

  if (!isLoading && campaigns.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-200 dark:border-gray-800 px-4 py-3">
      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
        <Target size={16} className="text-secondary shrink-0" />
        <span>Find contacts that match a campaign&apos;s ideal customer profile</span>
      </div>
      <CommonSelectComponent
        value=""
        options={options}
        placeholder={isLoading ? "Loading campaigns..." : "Choose a campaign"}
        ariaLabel="Find contacts for a campaign"
        width="w-[240px]"
        disabled={disabled || isLoading}
        onChange={(id: string | null) => {
          const picked = campaigns.find((c) => String(c.id) === String(id));
          if (picked) onPick({ id: picked.id, name: picked.name || "Untitled campaign" });
        }}
      />
    </div>
  );
}
